import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  ArrowRight,
  Radio,
  AlertOctagon,
  AlertTriangle,
  AlertCircle,
  MapPin,
  RefreshCw,
  Layers,
  Activity,
  ShieldCheck,
  ChevronRight,
  Flame,
  CloudRain,
  Wind,
  Compass,
  Zap,
  Clock,
  History,
  Waves,
  Volume2,
  Bell,
  Globe,
} from 'lucide-react';
import { NWSAdapter } from '../services/nwsAdapter';
import { USGSAdapter } from '../services/usgsAdapter';
import { UKAdapter } from '../services/ukAdapter';
import { CanadaAdapter } from '../services/canadaAdapter';
import { NotificationService } from '../services/notificationService';
import { AlertHistoryService } from '../services/alertHistoryService';
import { NormalizedAlert, AlertResponseMetadata, CountryCode } from '../types/alert';
import {
  US_LOCATIONS,
  CANADA_LOCATIONS,
  UK_LOCATIONS,
  AUSTRALIA_LOCATIONS,
  ALL_LOCATIONS,
  COUNTRY_CONFIGS,
  findLocation,
} from '../services/locationData';
import { AlertCard } from '../components/alerts/AlertCard';
import { AlertMap } from '../components/map/AlertMap';
import { FreshnessIndicator } from '../components/common/FreshnessIndicator';
import { DisclaimerBanner } from '../components/common/DisclaimerBanner';
import { AdSlot } from '../components/common/AdSlot';
import { AffiliateSection } from '../components/common/AffiliateCard';
import { getSeverityRank } from '../utils/formatters';

export const HomePage: React.FC = () => {
  const [weatherAlerts, setWeatherAlerts] = useState<NormalizedAlert[]>([]);
  const [earthquakes, setEarthquakes] = useState<NormalizedAlert[]>([]);
  const [ukAlerts, setUkAlerts] = useState<NormalizedAlert[]>([]);
  const [canadaAlerts, setCanadaAlerts] = useState<NormalizedAlert[]>([]);
  const [metadata, setMetadata] = useState<AlertResponseMetadata | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');

  // Selected Country Market
  const [activeCountry, setActiveCountry] = useState<CountryCode>(() => {
    try {
      const stored = localStorage.getItem('sn_selected_country');
      if (stored && stored in COUNTRY_CONFIGS) return stored as CountryCode;
    } catch {}
    return 'US';
  });

  // Listen to international country selector events from Header
  useEffect(() => {
    const handleCountryEvent = (e: any) => {
      if (e.detail && e.detail.country) {
        setActiveCountry(e.detail.country);
      }
    };
    window.addEventListener('sn_country_changed', handleCountryEvent);
    return () => window.removeEventListener('sn_country_changed', handleCountryEvent);
  }, []);

  // Real-time Auto-Sync countdown (every 45s)
  const [autoSyncEnabled, setAutoSyncEnabled] = useState<boolean>(true);
  const [secondsUntilSync, setSecondsUntilSync] = useState<number>(45);
  const navigate = useNavigate();

  const loadAllData = async (force: boolean = false) => {
    try {
      if (force) setIsRefreshing(true);
      else setLoading(true);
      setError(null);

      // Concurrent fetch from NWS, USGS, UK Environment Agency, and Canada ECCC
      const [nwsRes, usgsRes, ukRes, caRes] = await Promise.allSettled([
        NWSAdapter.fetchActiveAlerts({ forceRefresh: force }),
        USGSAdapter.fetchEarthquakes({ forceRefresh: force }),
        UKAdapter.fetchAlerts({ forceRefresh: force }),
        CanadaAdapter.fetchAlerts({ forceRefresh: force }),
      ]);

      let nwsList: NormalizedAlert[] = [];
      let usgsList: NormalizedAlert[] = [];
      let ukList: NormalizedAlert[] = [];
      let caList: NormalizedAlert[] = [];

      if (nwsRes.status === 'fulfilled') {
        nwsList = nwsRes.value.alerts;
        setWeatherAlerts(nwsList);
        setMetadata(nwsRes.value.metadata);
      }

      if (usgsRes.status === 'fulfilled') {
        usgsList = usgsRes.value.alerts;
        setEarthquakes(usgsList);
      }

      if (ukRes.status === 'fulfilled') {
        ukList = ukRes.value.alerts;
        setUkAlerts(ukList);
      }

      if (caRes.status === 'fulfilled') {
        caList = caRes.value.alerts;
        setCanadaAlerts(caList);
      }

      const combined = [...nwsList, ...usgsList, ...ukList, ...caList];
      AlertHistoryService.recordAlerts(combined);
      NotificationService.processNewAlerts(combined);
    } catch (err: any) {
      setError(err.message || 'Failed to poll live disruption data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
      setSecondsUntilSync(45);
    }
  };

  useEffect(() => {
    loadAllData(false);
  }, []);

  // Auto-sync ticker interval
  useEffect(() => {
    if (!autoSyncEnabled) return;
    const interval = setInterval(() => {
      setSecondsUntilSync((prev) => {
        if (prev <= 1) {
          loadAllData(true);
          return 45;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [autoSyncEnabled]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchInput.trim();
    if (!query) return;

    const loc = findLocation(query, activeCountry);
    if (loc) {
      navigate(`/location/${loc.code}`);
    } else {
      navigate(`/alerts?q=${encodeURIComponent(query)}`);
    }
  };

  // Combine weather alerts, international warnings and seismic events
  const allDisruptions = useMemo(() => {
    return [...weatherAlerts, ...earthquakes, ...ukAlerts, ...canadaAlerts];
  }, [weatherAlerts, earthquakes, ukAlerts, canadaAlerts]);

  // Filter disruptions for the active country market
  const countryFilteredDisruptions = useMemo(() => {
    if (activeCountry === 'GLOBAL') return allDisruptions;
    return allDisruptions.filter((a) => {
      if (a.country === activeCountry) return true;
      if (activeCountry === 'US' && !a.country) return true; // default NWS
      return false;
    });
  }, [allDisruptions, activeCountry]);

  const extremeAlerts = countryFilteredDisruptions.filter((a) => a.severity === 'Extreme');
  const severeAlerts = countryFilteredDisruptions.filter((a) => a.severity === 'Severe');

  // Filter alerts by chosen category pill
  const displayedAlerts = useMemo(() => {
    let list = [...countryFilteredDisruptions];
    if (categoryFilter === 'Extreme') {
      list = list.filter((a) => a.severity === 'Extreme');
    } else if (categoryFilter === 'Severe') {
      list = list.filter((a) => a.disruptionType === 'Severe');
    } else if (categoryFilter === 'Flood') {
      list = list.filter((a) => a.disruptionType === 'Flood');
    } else if (categoryFilter === 'Wildfire') {
      list = list.filter((a) => a.disruptionType === 'Wildfire');
    } else if (categoryFilter === 'Winter') {
      list = list.filter((a) => a.disruptionType === 'Winter');
    } else if (categoryFilter === 'Marine') {
      list = list.filter((a) => a.disruptionType === 'Marine');
    } else if (categoryFilter === 'Earthquake') {
      list = list.filter((a) => a.disruptionType === 'Earthquake');
    }

    return list.sort((a, b) => getSeverityRank(b.severity) - getSeverityRank(a.severity));
  }, [countryFilteredDisruptions, categoryFilter]);

  const topPriorityAlerts = displayedAlerts.slice(0, 6);
  const activeConfig = COUNTRY_CONFIGS[activeCountry] || COUNTRY_CONFIGS.US;

  // Country quick jump regions
  const quickJumpLocations = useMemo(() => {
    if (activeCountry === 'CA') return CANADA_LOCATIONS.slice(0, 8);
    if (activeCountry === 'GB') return UK_LOCATIONS.slice(0, 6);
    if (activeCountry === 'AU') return AUSTRALIA_LOCATIONS.slice(0, 6);
    return [
      { code: 'TX', name: 'Texas' },
      { code: 'FL', name: 'Florida' },
      { code: 'CA', name: 'California' },
      { code: 'OK', name: 'Oklahoma' },
      { code: 'NY', name: 'New York' },
      { code: 'NC', name: 'North Carolina' },
      { code: 'IL', name: 'Illinois' },
      { code: 'WA', name: 'Washington' },
    ];
  }, [activeCountry]);

  return (
    <div className="space-y-8 pb-16">
      {/* EMERGENCY BROADCAST BANNER (IF EXTREME HAZARDS EXIST) */}
      {extremeAlerts.length > 0 && (
        <div className="bg-red-600 text-white px-4 py-2.5 font-sans font-bold text-xs sm:text-sm flex flex-wrap items-center justify-between gap-2 shadow-lg animate-pulse">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-white inline-block animate-ping" />
            <span className="uppercase tracking-wider font-mono">
              [CRITICAL EMERGENCY] {extremeAlerts.length} EXTREME LIFE-SAFETY BULLETIN{extremeAlerts.length > 1 ? 'S' : ''} ACTIVE IN {activeConfig.name.toUpperCase()}
            </span>
            <span className="hidden md:inline font-normal">•</span>
            <span className="hidden md:inline font-normal truncate max-w-xl">
              {extremeAlerts[0].event}: {extremeAlerts[0].areaDesc}
            </span>
          </div>

          <Link
            to={`/alert/${encodeURIComponent(extremeAlerts[0].id)}`}
            className="px-2.5 py-1 bg-white text-red-700 font-bold rounded hover:bg-red-50 text-xs transition-colors flex items-center gap-1 flex-shrink-0"
          >
            <span>Read Emergency Order</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* MISSION CONTROL HERO BANNER */}
      <section className="relative bg-gradient-to-b from-navy-950 via-slate-950 to-navy-950 text-white border-b border-slate-800 overflow-hidden pt-8 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:24px_24px] opacity-40 pointer-events-none" />
        <div className="absolute top-1/4 right-5 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto space-y-8">
          {/* Top Live Status Indicator Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/90 border border-slate-700/80 backdrop-blur-md shadow-xs">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <span className="text-xs font-mono font-semibold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                <span>{activeConfig.flag}</span>
                <span>{activeConfig.name} Disruption Radar Active</span>
              </span>
              <span className="text-slate-600">•</span>
              <span className="text-xs text-slate-300 font-mono">
                {loading ? 'SYNCHRONIZING...' : `${countryFilteredDisruptions.length} ACTIVE DISRUPTIONS`}
              </span>
            </div>

            {/* Auto-Sync Toggle Control */}
            <div className="flex items-center gap-2.5 text-xs font-mono text-slate-300 bg-slate-900/80 px-3 py-1 rounded-xl border border-slate-800">
              <span className="inline-flex items-center gap-1.5 text-cyan-400 font-semibold">
                <Clock className="w-3.5 h-3.5" />
                <span>SYNC IN {secondsUntilSync}s</span>
              </span>
              <button
                onClick={() => setAutoSyncEnabled(!autoSyncEnabled)}
                className={`text-[10px] font-bold px-2 py-0.5 rounded transition-colors ${
                  autoSyncEnabled
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                    : 'bg-slate-800 text-slate-400'
                }`}
              >
                {autoSyncEnabled ? 'AUTO ON' : 'PAUSED'}
              </button>
            </div>
          </div>

          {/* Headline & Subhead */}
          <div className="max-w-3xl space-y-3">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight font-sans">
              {activeConfig.name} Live Disruption &amp; <br />
              <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-emerald-400 bg-clip-text text-transparent">
                Hazard Radar
              </span>
            </h1>
            <p className="text-slate-300 text-sm sm:text-base lg:text-lg leading-relaxed font-sans max-w-2xl font-normal">
              Continuous live telemetry aggregating official emergency bulletins from {activeConfig.primaryAgency}, real-time USGS seismic monitoring, and live precipitation Doppler radar.
            </p>
          </div>

          {/* High-Tech Search Console */}
          <div className="max-w-3xl space-y-3">
            <form onSubmit={handleSearch} className="relative group">
              <div className="flex flex-col sm:flex-row gap-2 bg-slate-900/90 p-2 rounded-2xl border border-slate-700/80 shadow-2xl backdrop-blur-xl group-focus-within:border-cyan-500/80 transition-all">
                <div className="relative flex-grow flex items-center">
                  <Search className="w-5 h-5 text-slate-400 absolute left-4 pointer-events-none" />
                  <input
                    type="text"
                    placeholder={`Search in ${activeConfig.name} by state, province, county or city...`}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-transparent text-white placeholder:text-slate-400 text-sm sm:text-base focus:outline-none font-medium"
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-xl text-sm transition-all shadow-lg flex items-center justify-center gap-2 flex-shrink-0"
                >
                  <span>Scan Area</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Quick Region Chips */}
            <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
              <span className="text-slate-400 font-semibold uppercase text-[11px]">Quick Jump:</span>
              {quickJumpLocations.map((st) => (
                <Link
                  key={st.code}
                  to={`/location/${st.code}`}
                  className="px-2.5 py-1 rounded-md bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700/70 hover:border-cyan-500/50 transition-all flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span>{st.name}</span>
                </Link>
              ))}
              <Link to="/locations" className="text-cyan-400 hover:underline ml-1">
                All Regions &rarr;
              </Link>
            </div>
          </div>

          {/* TELEMETRY METRIC TILES */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pt-4 border-t border-slate-800/80">
            {/* Total */}
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-1">
                <span>TOTAL DISRUPTIONS</span>
                <Radio className="w-4 h-4 text-cyan-400" />
              </div>
              <div className="text-3xl font-black text-white font-mono tracking-tight">
                {loading ? '...' : countryFilteredDisruptions.length}
              </div>
              <div className="text-[11px] text-slate-400 font-sans mt-0.5">
                {activeConfig.name} Live Feeds
              </div>
            </div>

            {/* Extreme */}
            <div className="bg-red-950/20 backdrop-blur-md border border-red-900/40 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between text-red-400 text-xs font-mono mb-1">
                <span className="font-semibold">EXTREME HAZARDS</span>
                <AlertOctagon className="w-4 h-4 text-red-500" />
              </div>
              <div className="text-3xl font-black text-red-400 font-mono tracking-tight">
                {loading ? '...' : extremeAlerts.length}
              </div>
              <div className="text-[11px] text-red-300/80 font-sans mt-0.5">Immediate danger to life</div>
            </div>

            {/* Severe */}
            <div className="bg-orange-950/20 backdrop-blur-md border border-orange-900/40 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between text-orange-400 text-xs font-mono mb-1">
                <span className="font-semibold">SEVERE IMPACTS</span>
                <AlertTriangle className="w-4 h-4 text-orange-500" />
              </div>
              <div className="text-3xl font-black text-orange-400 font-mono tracking-tight">
                {loading ? '...' : severeAlerts.length}
              </div>
              <div className="text-[11px] text-orange-300/80 font-sans mt-0.5">Significant disruption</div>
            </div>

            {/* Seismic Activity Tile */}
            <div className="bg-purple-950/20 backdrop-blur-md border border-purple-900/40 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between text-purple-400 text-xs font-mono mb-1">
                <span className="font-semibold">USGS EARTHQUAKES</span>
                <Activity className="w-4 h-4 text-purple-400" />
              </div>
              <div className="text-3xl font-black text-purple-400 font-mono tracking-tight">
                {loading
                  ? '...'
                  : countryFilteredDisruptions.filter((a) => a.disruptionType === 'Earthquake').length}
              </div>
              <div className="text-[11px] text-purple-300/80 font-sans mt-0.5">M2.5+ past 24 hours</div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTAINER */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        {/* Freshness Bar */}
        <FreshnessIndicator
          metadata={metadata}
          onRefresh={() => loadAllData(true)}
          isRefreshing={isRefreshing}
        />

        {/* Public Disclaimer Banner */}
        <DisclaimerBanner />

        {/* GEOSPATIAL RADAR MAP SECTION WITH AUTOMATIC RECENTERING */}
        <section aria-labelledby="radar-map-heading" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 animate-pulse" />
                <h2 id="radar-map-heading" className="text-xl sm:text-2xl font-black text-navy-950 font-sans">
                  {activeConfig.name} Disruption &amp; Precipitation Doppler Radar
                </h2>
              </div>
              <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                Live OpenStreetMap base with RainViewer animated Doppler radar loop and plotted emergency zones for {activeConfig.name}.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/history"
                className="px-3 py-1.5 text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <History className="w-3.5 h-3.5" />
                <span>24h Cleared Archive</span>
              </Link>

              <Link
                to="/alerts"
                className="px-3.5 py-1.5 text-xs font-bold text-navy-900 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors shadow-2xs flex items-center gap-1.5"
              >
                <span>View All Bulletins</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {error ? (
            <div className="p-8 bg-red-50 border border-red-200 rounded-2xl text-center space-y-3">
              <AlertTriangle className="w-8 h-8 text-red-600 mx-auto" />
              <h3 className="text-base font-bold text-red-950">Connection Error</h3>
              <p className="text-xs text-red-800 max-w-md mx-auto">{error}</p>
              <button
                onClick={() => loadAllData(true)}
                className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry Feeds
              </button>
            </div>
          ) : (
            <AlertMap
              key={`map_${activeCountry}`}
              alerts={countryFilteredDisruptions}
              center={activeConfig.center}
              zoom={activeConfig.zoom}
              height="520px"
            />
          )}
        </section>

        {/* Reserved Ad Slot 1 */}
        <AdSlot slotId="homepage-leaderboard" format="horizontal" />

        {/* FILTERABLE ACTIVE DISRUPTION CARDS */}
        <section aria-labelledby="active-radar-cards-heading" className="space-y-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <h2 id="active-radar-cards-heading" className="text-xl sm:text-2xl font-black text-navy-950">
                Active Bulletins &amp; Events ({activeConfig.name})
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                Filtered and ranked by deterministic severity and official emergency urgency.
              </p>
            </div>

            {/* Smart Category Filter Tabs */}
            <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-100 rounded-xl border border-slate-200 text-xs font-semibold">
              <button
                onClick={() => setCategoryFilter('ALL')}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  categoryFilter === 'ALL'
                    ? 'bg-navy-950 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-navy-950'
                }`}
              >
                All ({countryFilteredDisruptions.length})
              </button>
              <button
                onClick={() => setCategoryFilter('Severe')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${
                  categoryFilter === 'Severe'
                    ? 'bg-orange-600 text-white shadow-2xs'
                    : 'text-orange-700 hover:bg-orange-50'
                }`}
              >
                <Wind className="w-3.5 h-3.5" />
                <span>Severe/Storms</span>
              </button>
              <button
                onClick={() => setCategoryFilter('Flood')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${
                  categoryFilter === 'Flood'
                    ? 'bg-blue-600 text-white shadow-2xs'
                    : 'text-blue-700 hover:bg-blue-50'
                }`}
              >
                <CloudRain className="w-3.5 h-3.5" />
                <span>Floods</span>
              </button>
              <button
                onClick={() => setCategoryFilter('Winter')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${
                  categoryFilter === 'Winter'
                    ? 'bg-cyan-600 text-white shadow-2xs'
                    : 'text-cyan-700 hover:bg-cyan-50'
                }`}
              >
                <span>Winter</span>
              </button>
              <button
                onClick={() => setCategoryFilter('Wildfire')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${
                  categoryFilter === 'Wildfire'
                    ? 'bg-red-600 text-white shadow-2xs'
                    : 'text-red-700 hover:bg-red-50'
                }`}
              >
                <Flame className="w-3.5 h-3.5" />
                <span>Wildfire</span>
              </button>
              <button
                onClick={() => setCategoryFilter('Earthquake')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${
                  categoryFilter === 'Earthquake'
                    ? 'bg-purple-600 text-white shadow-2xs'
                    : 'text-purple-700 hover:bg-purple-50'
                }`}
              >
                <Activity className="w-3.5 h-3.5" />
                <span>Earthquakes</span>
              </button>
              <button
                onClick={() => setCategoryFilter('Marine')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${
                  categoryFilter === 'Marine'
                    ? 'bg-teal-600 text-white shadow-2xs'
                    : 'text-teal-700 hover:bg-teal-50'
                }`}
              >
                <Waves className="w-3.5 h-3.5" />
                <span>Marine</span>
              </button>
              <button
                onClick={() => setCategoryFilter('Extreme')}
                className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1 ${
                  categoryFilter === 'Extreme'
                    ? 'bg-red-600 text-white shadow-2xs'
                    : 'text-red-700 hover:bg-red-50'
                }`}
              >
                <AlertOctagon className="w-3.5 h-3.5" />
                <span>Extreme ({extremeAlerts.length})</span>
              </button>
            </div>
          </div>

          {/* Cards Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-56 bg-slate-100 rounded-2xl animate-pulse border border-slate-200" />
              ))}
            </div>
          ) : displayedAlerts.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center space-y-2 max-w-md mx-auto">
              <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto" />
              <h3 className="font-bold text-navy-950 text-base">No Matching Active Bulletins</h3>
              <p className="text-xs text-slate-600">
                Zero active alerts currently recorded under category "{categoryFilter}" in {activeConfig.name}.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topPriorityAlerts.map((alert) => (
                <AlertCard key={alert.id} alert={alert} />
              ))}
            </div>
          )}

          {displayedAlerts.length > 6 && (
            <div className="pt-2 text-center">
              <Link
                to="/alerts"
                className="inline-flex items-center gap-2 px-6 py-3 bg-navy-950 hover:bg-navy-900 text-white font-bold text-xs rounded-xl transition-all shadow-md"
              >
                <span>View All {displayedAlerts.length} Active Disruption Events</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </section>

        {/* REGIONAL INTELLIGENCE DIRECTORY */}
        <section aria-labelledby="states-matrix-heading" className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-xs space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 id="states-matrix-heading" className="text-xl font-black text-navy-950 flex items-center gap-2">
                <span>{activeConfig.flag}</span>
                <span>{activeConfig.name} Regional Radars</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                Instant dedicated radar pages for states, provinces, and regions across {activeConfig.name}.
              </p>
            </div>

            <Link
              to="/locations"
              className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <span>Browse All International Regions</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
            {quickJumpLocations.map((loc) => (
              <Link
                key={loc.code}
                to={`/location/${loc.code}`}
                className="p-3 rounded-xl border border-slate-200/90 hover:border-cyan-500 hover:bg-blue-50/40 hover:shadow-2xs transition-all flex items-center justify-between group"
              >
                <div className="truncate">
                  <div className="font-bold text-xs text-navy-950 group-hover:text-blue-600 truncate font-sans">
                    {loc.name}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400">
                    {loc.code}
                  </div>
                </div>
                <span className="font-mono font-bold text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  {loc.code}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* EMERGENCY PREPAREDNESS EQUIPMENT */}
        <AffiliateSection />

        {/* DATA SOURCES & INTEGRITY NOTICE */}
        <section className="bg-slate-900 text-slate-300 rounded-2xl p-6 sm:p-8 border border-slate-800 space-y-3">
          <div className="flex items-center gap-2 text-cyan-400 text-xs font-mono font-semibold uppercase tracking-wider">
            <Activity className="w-4 h-4" />
            <span>Multi-National Official Data Pipeline</span>
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-white">
            Ingestion: US NWS, USGS, Environment Canada &amp; UK Environment Agency
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-4xl">
            SignalNear aggregates verified open government data feeds from the United States (NOAA NWS, USGS Earthquake Hazards), Canada (Environment and Climate Change Canada), and the United Kingdom (Environment Agency). We do not rewrite or distort official bulletin text.
          </p>
          <div className="pt-2 flex flex-wrap gap-4 text-xs font-semibold">
            <Link to="/about" className="text-cyan-400 hover:underline flex items-center gap-1">
              <span>Read Full Methodology</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/history" className="text-amber-400 hover:underline flex items-center gap-1">
              <span>24-Hour Cleared Archive</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/sources" className="text-slate-400 hover:text-white flex items-center gap-1">
              <span>Verified Data Pipeline</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};
